import { Component, Vue, Prop, Watch, Inject } from 'vue-property-decorator';
import ErrorAlert from '@/components/errors/ErrorAlert.vue';
import AddonItem from '@/components/addons/AddonItem.vue';
import DonationInput from '@/components/inputs/donation/DonationInput.vue';
import QuantityInput from '@/components/inputs/quantity/QuantityInput.vue';
import ValidationErrors from '@/components/errors/ValidationErrors.vue';
import { DtoTicket, Ticket } from '@/common/types/event/DtoTicket';
import { createAttendee } from '@/common/types/event/DtoAttendee';
import '@/common/filters/AllFiltersExport';
import { TicketsAndAddons } from '../../common/types/event/DtoTicketsAndAddons';
import { EventRegistration } from '../../common/types/event/DtoEventRegistration';
import { Addon, createAddon } from '@/common/types/event/DtoAddon';
import { DtoCurrentUser } from '@/common/types/user/DtoCurrentUser';
import { createProductQuantity, createProductQuantityFromProduct } from '@/common/types/ecommerce/DtoProductQuantity';
import triggerPopover from '../directives/triggerPopover-directive';
import { TicketQuantity } from '@/common/types/event/TicketQuantity';
import { TicketType } from '../../common/types/event/TicketType';
import PhoneInput from '@/components/inputs/phone/PhoneInput.vue';


@Component({
    name: 'EventRegistrationTickets',
    components: {
        ErrorAlert,
        ValidationErrors,
        AddonItem,
        DonationInput,
        QuantityInput,
        PhoneInput
    },
    directives: {
        triggerPopover
    }
})
export default class EventRegistrationTickets extends Vue {
    @Prop({ required: false })
    attendeeIndex!: number;

    @Prop({ required: true })
    isAttendeeCentered!: boolean;

    @Prop({ required: false })
    previouslyPurchasedTicketIDs: number[] = [];

    @Prop({ required: true })
    isLoading!: boolean;

    @Prop({ required: false, default: () => false })
    parentRequired!: boolean;

    @Prop({ required: false, default: () => 'Parent Member' })
    parentLabel!: string;

    @Inject('registration')
    registration!: EventRegistration;

    @Inject('ticketsAndAddons')
    ticketsAndAddons!: TicketsAndAddons;

    @Inject('user')
    currentUser!: DtoCurrentUser;

    @Inject('validator')
    $validator: any;


    attendeeTicketsCategories: { id: number, category: string, description: string, isCollapsed: boolean, ticketQuantities: TicketQuantity[] }[] = [];
    attendeeAddons: Addon[] = [];
    

    created() {
        if (this.isAttendeeCentered) {
            this.initAttendeeData()
        }
    }

    get showCeu() {
        return this.ticketsAndAddons.hasCeus;
    }

    get showQuantityField() {
        return !this.isAttendeeCentered;
    }

    get showPrice() {
        return !this.ticketsAndAddons.allItemsAreFree;
    }

    get showTicketSelectionField() {
        return this.isAttendeeCentered;
    }

    get ticketOptionsTitle() {
        return this.isAttendeeCentered ? "Tickets" : "Registration Options";
    }

    get showAddons() {
        if (!this.attendeeAddons || this.attendeeAddons.length === 0) {
            return false;
        }
        return !(this.attendeeAddons.every(a => a.product.hasPurchaseWindow && (a.product.isOnlyVisibleInPurchaseWindow && !a.product.isWithinPurchaseWindow)));
    }

    get allTicketQuantities() {
        let tq: TicketQuantity[] = [];

        this.attendeeTicketsCategories.forEach(function (category) {
            tq = tq.concat(category.ticketQuantities);
        });

        return tq;
    }

    get purchasedTicketIDs() {
        return this.allTicketQuantities.filter(tq => tq.quantity > 0).map(tq => tq.ticket.id);
    }

    get subTotal() {
        return this.ticketSubtotal + this.addonSubtotal;
    }

    get subTotalFormated() {
        return this.registration.currencyIcon + (this.subTotal).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    get ticketSubtotal() {
        var ticketTotal = 0;
        var self = this;

        this.allTicketQuantities
            .filter(tq => tq.quantity > 0)
            .forEach(function (ticketValue) {
                var price = self.registration.getTicketPrice(ticketValue.ticket, self.getCurrentAttendeeOrEmptyAttendee());
                var quantity = ticketValue.quantity || 0;
                ticketTotal += price * quantity;
            });

        return ticketTotal;
    }

    get addonSubtotal() {
        var addonTotal = 0;
        this.attendeeAddons.forEach(function (addon) {
            addonTotal += addon.addonSubTotal;
        });
        return addonTotal;
    }

    get showCategoryHeader() {
        return this.ticketsAndAddons.ticketCategories.length > 1
            || this.ticketsAndAddons.ticketCategories.some(x => x.name != "");
    }

    get hasVisibleTickets() {
        return this.ticketsAndAddons.ticketCategories.length > 0;
    }

    get getCurrency() {
        return "<i class='fa-solid fa-euro-sign'></i>";
    }

    getCurrentAttendeeOrEmptyAttendee() {
        if (this.registration && this.registration.attendees && this.registration.attendees.length > 0) {
            return this.registration.attendees[this.attendeeIndex];
        }
        return createAttendee();
    }

    getTicketCeu(ticket: DtoTicket) {
        const ceuTypes = ticket.ceuTypes ? ' (' + ticket.ceuTypes + ')' : '';
        return (ticket.continuingEducationUnits || 0).toFixed(2) + ceuTypes;
    }

    getTicketPriceTierHtml(ticket: DtoTicket) {
        var result = "<table class='table table-bordered tickets'><thead><tr><th>Min Qty</th><th>Max Qty</th><th>Price Per Ticket</th></tr></thead><tbody>";
        var currencyIcon = this.registration.currencyIcon;
        ticket.ticketPricingTiers.forEach(function (tier) {
            var max = tier.maximum || "No Limit";
            result += "<tr><td>" + tier.minimum + "</td><td>" + max + "</td><td><strong>" + currencyIcon + tier.price.toFixed(2) + "</strong></td></tr>";
        });

        result += "</tbody></table>";
        return result;
    }

    updateAttendeeTicketSelected(ticketQuantity: TicketQuantity) {
        ticketQuantity.quantity = ticketQuantity.checked ? 1 : 0;
        this.$emit('check-unique-email', this.$validator);
    }

    isTicketLocked(ticket: DtoTicket) {
        let attendee = null;
        if (this.isAttendeeCentered) {
            attendee = this.getCurrentAttendeeOrEmptyAttendee();

            // Ineligible for ticket
            if (attendee.ineligibleTickets.includes(ticket.id) || (attendee.isGuest && !ticket.allowGuestAttendee)) {
                ticket.restrictionTitle = "Restricted";
                ticket.restrictionMessage = attendee.name + " is not eligible to purchase this ticket.";
                return true;
            }

            // Already purchased ticket
            if (!this.isEmpty(attendee.alreadyPurchasedTickets)) {
                if (attendee.alreadyPurchasedTickets.includes(ticket.id)) {
                    ticket.restrictionTitle = "Restricted";
                    ticket.restrictionMessage = attendee.name + " has already purchased this ticket.";
                    return true;
                }
            }

            if (ticket.numberLeft <= this.registration.getTicketCount(ticket) && !attendee.hasSelectedTicket(ticket.id)) {
                ticket.restrictionTitle = "Sold Out";
                ticket.restrictionMessage = "There are no more tickets available for purchase.";
                return true;
            }
        }

        if (ticket.restrictionMessage) {
            if (ticket.unlockable && ticket.dependentTicketTypes?.length) {
                // Collect all ticket IDs to check against, starting with currently selected tickets
                const ticketIDs = new Set(this.purchasedTicketIDs);

                if (this.isAttendeeCentered) {
                    // Attendee Centric: append previously purchased tickets from current attendee
                    attendee?.alreadyPurchasedTickets?.forEach(id => ticketIDs.add(id));
                } else {
                    // Ticket Centric: append previously purchased tickets from all eligible attendees
                    this.previouslyPurchasedTicketIDs?.forEach(id => ticketIDs.add(id));
                }

                // Ticket is unlocked if at least one of the dependent tickets has been or will be purchased
                const isUnlocked = ticket.dependentTicketTypes.some(id => ticketIDs.has(id));

                // Return true if still restricted (not unlocked)
                return !isUnlocked;
            }

            // Ticket is not unlockable
            return true;
        }

        // No restrictions
        return false;
    }

    @Watch("attendeeTicketsCategories", { deep: true })
    @Watch("attendeeAddons", { deep: true })
    updateTicketCenteredRegistration() {
        if (this.isAttendeeCentered) {
            return;
        }

        var self = this;
        var existingAttendees = self.registration.attendees;
        self.registration.attendees = [];


        this.allTicketQuantities.forEach(function (ticketQuantity) {
            if (!self.isTicketLocked(ticketQuantity.ticket)) {
                ticketQuantity.unlockTicket();

                var existingAttendeesForTicket = existingAttendees.filter(attendee => attendee.hasSelectedTicket(ticketQuantity.ticket.id));

                for (var i = 0; i < ticketQuantity.quantity * ticketQuantity.ticket.seatsPerTicket; i++) {
                    var attendee = existingAttendeesForTicket[i] || createAttendee({ selectedTickets: [ticketQuantity.ticket] });
                    var attendeeTicket = attendee.selectedTickets[0];

                    // We need to set price for donations manually
                    if (attendeeTicket.ticketType == TicketType.Donation) {
                        attendeeTicket.price = ticketQuantity.ticket.price;
                    }
                    self.registration.attendees.push(attendee);
                }
            }
            else {
                ticketQuantity.lockTicket();
            }
        });

        this.registration.selectedAddons = this.attendeeAddons
            .filter(pq => pq.quantity.quantity > 0)
            .map(pq => pq.quantity);
    }

    @Watch("attendeeTicketsCategories", { deep: true })
    @Watch("attendeeAddons", { deep: true })
    updateAttendeeCenteredRegistration() {
        if (!this.isAttendeeCentered) {
            return;
        }

        var self = this;
        var attendee = this.getCurrentAttendeeOrEmptyAttendee();

        attendee.selectedTickets = [];

        this.allTicketQuantities.forEach(function (ticketQuantity) {
            if (!self.isTicketLocked(ticketQuantity.ticket)) {
                ticketQuantity.unlockTicket();

                if (ticketQuantity.quantity > 0) {
                    attendee.selectedTickets.push(new Ticket(ticketQuantity.ticket));
                }
            }
            else {
                ticketQuantity.lockTicket();
            }
        });

        attendee.selectedAddons = this.attendeeAddons
            .filter(pq => pq.quantity.quantity > 0)
            .map(pq => pq.quantity);
    }

    getSeatsRemaining(ticketQuantity: TicketQuantity) {
        // Ticket centered events never have partially filled tables
        if (!this.isAttendeeCentered) {
            return ticketQuantity.ticket.seatsPerTicket;
        }

        var quantity = this.registration.getAttendeeCount(ticketQuantity.ticket.id);
        var seatsAvailable = ticketQuantity.ticket.seatsPerTicket;
        var seatsTaken = quantity % seatsAvailable;
        var seatsRemaining = seatsAvailable - seatsTaken;

        if (seatsRemaining == seatsAvailable) {
            seatsRemaining = ticketQuantity.checked ? 0 : seatsAvailable;
        }

        return seatsRemaining;
    }

    @Watch("attendeeIndex", { immediate: true })
    @Watch("isLoading", { immediate: true })
    initAttendeeData() {
        var self = this;
        this.attendeeTicketsCategories = [];
        this.attendeeAddons = [];
        var currentAttendee = this.getCurrentAttendeeOrEmptyAttendee();

        this.ticketsAndAddons.ticketCategories.forEach(category => {
            self.attendeeTicketsCategories.push({
                id: category.id,
                category: category.name,
                description: category.description,
                isCollapsed: category.collapseByDefault,
                ticketQuantities: category.tickets.map(function (dtoTicket: DtoTicket) {

                    // Attendee Centric
                    if (self.isAttendeeCentered) {
                        var foundTicket = currentAttendee.selectedTickets.find(x => x.id == dtoTicket.id);
                        if (foundTicket) {
                            return new TicketQuantity({ ticket: foundTicket, quantity: 1, checked: true, locked: false });
                        }

                        // We need to delink the ticket when doing this mapping in case its a donation.
                        return new TicketQuantity({ ticket: new Ticket(dtoTicket), quantity: 0, checked: false, locked: false });
                    }

                    var ticket = new Ticket(dtoTicket);

                    if (dtoTicket.ticketType == TicketType.Donation) {
                        // We need to find any attendee with this ticket because all of them will have the same amount
                        var attendee = self.registration.attendees.find(attendee => attendee.hasSelectedTicket(ticket.id));
                        if (attendee) {
                            // Find donation ticket price
                            var donationTicket = attendee.selectedTickets.find(attendeeTicket => attendeeTicket.id == ticket.id);
                            if (donationTicket) {
                                ticket.price = donationTicket.price;
                            }
                        }
                    }

                    // Ticket Centric
                    var quantity = self.registration.getTicketCount(dtoTicket);
                    return new TicketQuantity({ ticket: ticket, quantity: quantity, checked: false, locked: false });
                })
            });
        });

        // If there is only 1 ticket not locked then auto select it. 
        if (this.allTicketQuantities.filter(x => !this.isTicketLocked(x.ticket)).length == 1) {
            self.attendeeTicketsCategories
                .flatMap(category => category.ticketQuantities)
                .filter(x => !this.isTicketLocked(x.ticket))
                .forEach(ticket => {
                    ticket.quantity = 1;
                    ticket.checked = true
                });
        }

        // Populate attendeeAddons - it gets mapped to registration.selectedAddons (ticket based) or registration.attendees[this.attendeeIndex].selectedAddons (attendee based)        
        if (this.ticketsAndAddons.products && this.ticketsAndAddons.products.length > 0) {

            // If the user navigates back to this page, then this attendee may already have some selections
            self.attendeeAddons = this.ticketsAndAddons.products.map(function (product) {
                var existing = self.isAttendeeCentered ?
                    currentAttendee.selectedAddons.find(pq => pq.id == product.id) :  // attendee based - addons live on the attendee
                    self.registration.selectedAddons.find(pq => pq.id == product.id); // ticket based   - addons live on the registration

                return createAddon({
                    product: product,
                    quantity: existing ? createProductQuantity(existing) : createProductQuantityFromProduct(product, self.currentUser.isMember)
                }, self.currentUser.isMember);
            });
        }
    }

    generateCategoryCollapseId(id: number) {
        if (!id) {
            return "nonecat";
        }
        return id + "-category-container";
    }

    private isEmpty(array: any[]) {
        return !array || !array.length;
    }

    // Manually adjust the collapse value timeout to avoid conflict with UI framework handling collapse state
    private handleHeaderToggleAccessibility(category: { isCollapsed: boolean }): void {
        setTimeout(() => {
            category.isCollapsed = !category.isCollapsed;
        }, 200);
    }

    shouldShowCountdown(ticket: DtoTicket): boolean {
        if (
            ticket.numberLeft == null ||
            ticket.numberAvailable == null ||
            ticket.numberAvailable == 0 ||
            ticket.countdownPercent == 100
        ) {
            return false;
        }

        const percentRemaining = ((ticket.numberAvailable - ticket.numberLeft) / ticket.numberAvailable) * 100;
        return percentRemaining >= ticket.countdownPercent;
    }
}